import { useState, useEffect } from "react";
import { BASE_URL } from "../../global";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import {
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Toolbar,
  Button,
  Tooltip,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useParams, useNavigate } from "react-router-dom";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import ErrorModal from "../../utils/components/errorModal";
import EditWrap from "../commonComponent/editWrap";

const emptyForm = () => ({
  clientId:         null,
  addressId:        null,
  serviceRequestNo: "",
  quantity:         "",
  dueDate:          null,
});

export default function EditIndependentDue(props) {
  const { setLoader } = props;
  const params   = useParams();
  const navigate = useNavigate();
  const isEdit   = params?.id && params.id !== "0";

  const [clientArray,  setClientArray]  = useState([]);
  const [addressArray, setAddressArray] = useState([]);
  const [form, setForm]                 = useState(emptyForm());

  const [errormodalIsOpen, setErrorModalIsOpen] = useState(false);
  const [errorMessage,     setErrorMessage]     = useState(null);

  const openErrorModal = (msg, trace, file) => {
    setErrorMessage({ errorMessage: msg, stackTrace: trace, fileName: file });
    setErrorModalIsOpen(true);
  };
  const closeErrorModal = () => setErrorModalIsOpen(false);

  const editAccess = localStorage.getItem("editAccess")?.split(",").map(Number);

  const clientOptions = clientArray.map((c) => ({
    label: `${c.id}, ${c.companyName}${c.uniCode ? ` (${c.uniCode})` : ""}`,
    id: c.id,
  }));

  const addressOptions = addressArray.map((a) => ({
    label: a.address,
    id:    a.id,
  }));

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const fetchClients = () => {
    axiosWithToken
      .get(BASE_URL + "clients?_where=(status,eq,1)&_fields=id,companyName,uniCode&_sort=companyName")
      .then((res) => setClientArray(res.data || []))
      .catch((err) => {
        if (err.message !== "request_aborted") toast.error("Failed to load clients");
      });
  };

  const fetchAddresses = (clientId) => {
    if (!clientId) { setAddressArray([]); return; }
    axiosWithToken
      .post(BASE_URL + "dynamic", {
        query: `SELECT id, address FROM addresses
                WHERE FIND_IN_SET(id, (SELECT address FROM clients WHERE id = ${clientId}))`,
      })
      .then((res) => setAddressArray(res.data || []))
      .catch((err) => {
        if (err.message !== "request_aborted") toast.error("Failed to load addresses");
      });
  };

  const fetchRecord = async (id) => {
    try {
      const res = await axiosWithToken.post(
        BASE_URL + "dynamic",
        {
          query: `SELECT ind.id, ind.clientId, ind.addressId,
                         ind.serviceRequestNo, ind.quantity, ind.dueDate
                  FROM independentDueList ind
                  WHERE ind.id = ${id}`,
        },
        { headers: { requestType: "fetchData" } }
      );
      const record = res.data?.[0];
      if (!record) { toast.error("Record not found"); return; }

      setForm({
        clientId:         record.clientId,
        addressId:        record.addressId ? Number(record.addressId) : null,
        serviceRequestNo: record.serviceRequestNo || "",
        quantity:         record.quantity ?? "",
        dueDate:          record.dueDate ? new Date(record.dueDate) : null,
      });
    } catch (err) {
      if (err.message !== "request_aborted")
        openErrorModal(err.message, "fetchRecord", "editIndependentDue");
    }
  };

  useEffect(() => {
    fetchClients();
    if (isEdit) fetchRecord(params.id);
  }, []);

  // reload addresses when client changes
  useEffect(() => {
    fetchAddresses(form.clientId);
    if (!isEdit) updateForm({ addressId: null });
  }, [form.clientId]);

  const validateData = () => {
    if (!form.clientId)                { toast.error("Please select a client");              return false; }
    if (!form.serviceRequestNo.trim()) { toast.error("Please enter Service Request Number"); return false; }
    if (!form.quantity)                { toast.error("Please enter Quantity");               return false; }
    if (!form.dueDate)                 { toast.error("Please select a Due Date");            return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateData()) return;
    if (setLoader) setLoader(true);

    const payload = {
      clientId:         form.clientId,
      addressId:        form.addressId ?? null,
      serviceRequestNo: form.serviceRequestNo.trim() || null,
      quantity:         form.quantity ? Number(form.quantity) : null,
      dueDate:          form.dueDate ? moment(form.dueDate).format("YYYY-MM-DD") : null,
    };

    try {
      if (isEdit) {
        await axiosWithToken.patch(BASE_URL + `independentDueList/${params.id}`, payload);
        toast.success("Record updated successfully!");
      } else {
        await axiosWithToken.post(BASE_URL + "independentDueList", payload);
        toast.success("Record created successfully!");
      }
      navigate("/independentDue");
    } catch (err) {
      if (err.message !== "request_aborted")
        openErrorModal(err.message, "handleSubmit", "editIndependentDue");
    } finally {
      if (setLoader) setLoader(false);
    }
  };

  return (
    <EditWrap>
      <Typography variant="h6" component="h6" style={{ float: "left" }}>
        {isEdit ? "Edit Independent Due" : "Create Independent Due"}
      </Typography>
      <br />
      <hr />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={clientOptions}
            value={clientOptions.find((o) => o.id === form.clientId) || null}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            getOptionLabel={(opt) => opt.label || ""}
            onChange={(_, val) => updateForm({ clientId: val?.id || null })}
            renderInput={(p) => <TextField {...p} label="Client *" />}
            disabled={!!isEdit}
          />
        </Grid>

        {/* address dropdown after seleccting client */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={addressOptions}
            value={addressOptions.find((o) => o.id === form.addressId) || null}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            getOptionLabel={(opt) => opt.label || ""}
            onChange={(_, val) => updateForm({ addressId: val?.id || null })}
            renderInput={(p) => <TextField {...p} label="Address" />}
            disabled={!form.clientId}
            noOptionsText={form.clientId ? "No addresses found" : "Select a client first"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            size="small"
            fullWidth
            label="Service Request Number *"
            value={form.serviceRequestNo}
            inputProps={{ autoComplete: "off" }}
            onChange={(e) => updateForm({ serviceRequestNo: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            size="small"
            fullWidth
            label="Quantity *"
            type="number"
            value={form.quantity}
            inputProps={{ autoComplete: "off", min: 1 }}
            onChange={(e) => {
              const val = e.target.value;
              // allow clearing the field so validation can catch an empty value
              if (val === "") { updateForm({ quantity: "" }); return; }
              const num = Math.max(1, Number(val) || 1);
              updateForm({ quantity: num });
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              slotProps={{ textField: { size: "small", fullWidth: true, label: "Due Date *" } }}
              value={form.dueDate}
              format="dd/MM/yyyy"
              onChange={(val) => updateForm({ dueDate: val })}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginTop: "16px",
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "grey" }}
          size="small"
          onClick={() => { if (window.confirm("Are you sure you want to cancel?")) window.history.back(); }}
        >
          Cancel
        </Button>

        <Tooltip
          title={
            !(editAccess?.includes(0) || editAccess?.includes(1))
              ? "You don't have access"
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              size="small"
              disabled={!(editAccess?.includes(0) || editAccess?.includes(1))}
              onClick={handleSubmit}
            >
              {isEdit ? "Update" : "Save"}
            </Button>
          </span>
        </Tooltip>
      </Toolbar>

      <ToastContainer />

      {errormodalIsOpen && (
        <ErrorModal
          errormodalIsOpen={errormodalIsOpen}
          closeErrorModal={closeErrorModal}
          errorMessage={errorMessage}
        />
      )}
    </EditWrap>
  );
}
